import { t } from 'i18next';
import { useMemo } from 'react';

import { ImportHeader, ImportType } from 'bom/enums';
import { IImportPart } from 'bom/types';
import { getImportHeaderLabels, getImportPartNotes } from 'bom/utils';
import { AgGridTable } from 'core/components';
import { DEFAULT_ROW_HEIGHT } from 'core/constants';
import { AgGridFilterOption } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { PricingHelper } from 'pricing/helpers';

interface IProps {
  type: ImportType;
  columns: string[];
  records: IImportPart[];
  isDataLoading: boolean;
  loadingPercentage: number;
  dataCurrentPage: number;
  floatingFilter?: boolean;
  rowHeight?: number;
  showQuoteLineMatchNotes?: boolean;
}

const getColumnHeader = (columnName: string, type: ImportType) => {
  const importHeaderLabels = getImportHeaderLabels(type);

  switch (columnName) {
    case ImportHeader.QUANTITY:
      return t('headerOriginalQuantity');
    default:
      return importHeaderLabels[columnName];
  }
};

const headerFilterMap: Record<string, string> = {
  [ImportHeader.NUMBER]: 'agTextColumnFilter',
  [ImportHeader.CUSTOMER_PART_NUMBER]: 'agTextColumnFilter',
  [ImportHeader.MANUFACTURER]: 'agSetColumnFilter',
  [ImportHeader.DESCRIPTION]: 'agTextColumnFilter',
  [ImportHeader.QUANTITY]: 'agNumberColumnFilter',
  [ImportHeader.PRICE]: 'agNumberColumnFilter',
  [ImportHeader.COST]: 'agNumberColumnFilter',
  [ImportHeader.COST_SOURCE]: 'agTextColumnFilter',
  [ImportHeader.VENDOR_QUOTE_NUMBER]: 'agTextColumnFilter',
  [ImportHeader.REPLACEMENT_COST]: 'agNumberColumnFilter',
  [ImportHeader.NOTES]: 'agTextColumnFilter',
  [ImportHeader.LEAD_TIME_NOTE]: 'agTextColumnFilter',
  [ImportHeader.UOM]: 'agSetColumnFilter',
};

const filterParams: Record<string, { defaultOption: AgGridFilterOption }> = {
  agNumberColumnFilter: {
    defaultOption: AgGridFilterOption.IN_RANGE,
  },
};

const rightAlignColumns = [
  ImportHeader.QUANTITY,
  ImportHeader.COST,
  ImportHeader.PRICE,
  ImportHeader.REPLACEMENT_COST,
  ImportHeader.UOM,
];

const pricingColumns = [ImportHeader.COST, ImportHeader.PRICE, ImportHeader.REPLACEMENT_COST];

export const PreviewImportTable = ({
  type,
  columns,
  records,
  isDataLoading,
  loadingPercentage,
  dataCurrentPage,
  floatingFilter = true,
  rowHeight = DEFAULT_ROW_HEIGHT,
  showQuoteLineMatchNotes = false,
}: IProps) => {
  const defaultColDef = useMemo(
    () => ({
      wrapHeaderText: true,
      autoHeaderHeight: true,
      suppressMovable: true,
      floatingFilter,
      sortable: true,
      maxWidth: 400,
      enableRowGroup: false,
    }),
    [floatingFilter]
  );

  const columnDefs = useMemo(() => {
    const hasImportNotes = records?.some(
      (record) => getImportPartNotes(record, showQuoteLineMatchNotes).length > 0
    );

    return [
      ...(columns?.map((column) => ({
        headerName: getColumnHeader(column, type),
        field: column,
        filter: headerFilterMap[column],
        filterParams: filterParams[headerFilterMap[column]] || {},
        type: rightAlignColumns.includes(column as ImportHeader) ? 'rightAligned' : '',
        ...(pricingColumns.includes(column as ImportHeader) && {
          valueFormatter: ({ value }) => PricingHelper.formatPricing({ amount: value }),
        }),
      })) ?? []),
      ...(hasImportNotes
        ? [
            {
              colId: 'importNotes',
              headerName: t('headerImportNotes'),
              valueGetter: ({ data }) =>
                data && getImportPartNotes(data, showQuoteLineMatchNotes).join('; '),
              tooltipValueGetter: (params) =>
                params?.data && getImportPartNotes(params.data, showQuoteLineMatchNotes).join('; '),
              filter: 'agTextColumnFilter',
            },
          ]
        : []),
    ];
  }, [columns, type, records, showQuoteLineMatchNotes]);

  return (
    <AgGridClientTableProvider
      tableId="previewImport"
      entityType="Part"
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      data={records}
      isDataLoading={isDataLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={dataCurrentPage}
      sideBar={{}}
      autoSizeStrategy={{
        type: 'fitCellContents',
      }}
      rowHeight={rowHeight}
    >
      <AgGridTable />
    </AgGridClientTableProvider>
  );
};
